import React, { FC, useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Warehouse, getWorkingScheduleString, useOpenWarehouseDoorMutation } from 'entities/Warehouse';
import { useGetAccessibleDoorsQuery } from 'entities/InternalEquipment';

import { ReactComponent as LocationIcon } from 'shared/assets/icons/LocationIcon.svg';
import { ReactComponent as WifiIcon } from 'shared/assets/icons/WifiIcon.svg';
import { ReactComponent as LockIcon } from 'shared/assets/icons/LockIcon.svg';
import { ReactComponent as KeyIcon } from 'shared/assets/icons/KeyIcon.svg';
import { ReactComponent as ClockIcon } from 'shared/assets/icons/ClockIcon.svg';
import { InfoTag } from 'shared/ui/InfoTag';
import { Button } from 'shared/ui/Button';
import { MessagePanel } from 'shared/ui/MessagePanel';
import { Modal } from 'shared/ui/Modal';
import { ShowOnMapButton } from 'features/ShowOnMapButton';
import { CopyTooltip } from 'shared/ui/CopyTooltip';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { ReadMore } from 'shared/ui/ReadMore';
import { WarnTag } from 'shared/ui/WarnTag';

interface WarehouseCardProps {
  warehouse: Nullable<Warehouse>;
  type: 'reservation' | 'contract';
}

export const WarehouseCard: FC<WarehouseCardProps> = (props) => {
  const { warehouse, type } = props;

  const { t } = useAppTranslation(['contracts', 'common', 'booking']);

  const [isModalOpened, setModalOpened] = useState(false);
  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const coords = useGeolocation();
  const [openWarehouseDoor, { isLoading: isOpenLoading }] = useOpenWarehouseDoorMutation();

  const warehouseId = warehouse?.warehouseId ?? '';
  const { data: accessibleDoors = [], isLoading: isDoorsLoading } = useGetAccessibleDoorsQuery(warehouseId, { skip: !warehouseId });

  const isReservation = type === 'reservation';
  const fullAddress = `${warehouse?.address ?? ''} ${warehouse?.zip ?? ''}`.trim();

  const hasDoors = (accessibleDoors?.length ?? 0) > 0;
  const hasMoreThanOneDoor = (accessibleDoors?.length ?? 0) > 1;

  const singleDoor = useMemo(() => (accessibleDoors?.length === 1 ? accessibleDoors[0] : null), [accessibleDoors]);

  const workingScheduleString = getWorkingScheduleString(warehouse?.workingSchedule || []);

  const handleCloseModal = useCallback((): void => {
    setModalOpened(false);
  }, []);
  const handleOpenModal = useCallback((): void => {
    setModalOpened(true);
  }, []);
  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);
  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const handleOpenDoor = useCallback(
    async (lockAddress: Nullable<string>): Promise<void> => {
      if (!warehouse || !lockAddress) return;

      try {
        await openWarehouseDoor({
          warehouseId: warehouse.warehouseId,
          args: { lockAddress, longitude: coords?.lng, latitude: coords?.lat },
        }).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('The door has been successfully opened', { ns: 'common' }));
      } catch (error: CustomAny) {
        const errorMessage = t(error?.data?.message?.[0] || 'Error while opening door', { ns: 'common' });
        showNotification('error', t('Error', { ns: 'common' }), errorMessage);
      }
    },
    [coords?.lat, coords?.lng, openWarehouseDoor, t, warehouse],
  );

  return (
    <div className="flex flex-col justify-between gap-6 desktop:flex-row">
      <div className="flex flex-col gap-2 desktop:w-1/2">
        <ReadMore html={getLocalizedString(warehouse?.description)} rows={3} />
        {warehouse?.warningInfo && (
          <div className="flex items-center text-primaryLight text-sm font-normal">
            <WarnTag textSize="small" className="font-semibold px-0" iconClassName="w-[18px] h-[18px]">
              <span className="text-sm font-semibold">{getLocalizedString(warehouse.warningInfo)}</span>
            </WarnTag>
          </div>
        )}
        <div className="flex items-center">
          <LocationIcon className="mr-2 stroke-accent" />
          <CopyTooltip className="text-accent underline cursor-pointer text-sm font-normal">{fullAddress}</CopyTooltip>
        </div>
        <div className="flex items-center text-accent text-sm font-normal">
          <ClockIcon className="mr-2 shrink-0 [&>path]:stroke-accent [&>circle]:stroke-accent w-[16px] h-[16px]" />
          {workingScheduleString}
        </div>

        {!isReservation && (
          <>
            <div className="flex items-center gap-4">
              <div className="flex items-center text-primaryLight text-sm font-normal break-all">
                <WifiIcon className="mr-2 stroke-accent shrink-0" />
                {warehouse?.wifiNetworkName}
              </div>
              <div className="flex items-center text-primaryLight text-sm font-normal break-all">
                <LockIcon className="mr-2 stroke-accent shrink-0" />
                <CopyTooltip className="text-accent underline cursor-pointer text-sm font-normal">{warehouse?.wifiPassword}</CopyTooltip>
              </div>
            </div>

            {warehouse?.videoGuideUrl && (
              <div className="my-5">
                <a href={warehouse?.videoGuideUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-accent">
                  &#128165;{t('How to use this warehouse?', { ns: 'booking' })}&#128165;
                </a>
              </div>
            )}

            <div className="mt-2 desktop:w-2/3">
              <InfoTag>{t('Connect to the warehouse`s WIFI or being no more than 500m')}</InfoTag>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2 desktop:w-1/4">
        {warehouse?.latitude && warehouse?.longitude && (
          <ShowOnMapButton latitude={warehouse.latitude} longitude={warehouse.longitude} theme="outlined" />
        )}

        {!isReservation && (
          <>
            {isDoorsLoading ? (
              <Button theme="outlined" isLoading className="pointer-events-none">
                {t('Loading...', { ns: 'common' })}
              </Button>
            ) : !hasDoors ? (
              <Button theme="outlined" isDisabled>
                {t('No available doors')}
              </Button>
            ) : hasMoreThanOneDoor ? (
              <Button icon={<KeyIcon className="stroke-white" />} iconPosition="prev" onClick={handleOpenModal}>
                {t('Doors or gates')}
              </Button>
            ) : (
              <Button icon={<KeyIcon className="stroke-white" />} iconPosition="prev" onClick={openConfirmModal}>
                {t('Open')}
              </Button>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpened={isOpenedConfirm}
        title={t('Open door', { ns: 'common' })}
        description={
          singleDoor
            ? t('Are you sure you want to open door {{name}}', { ns: 'common', name: getLocalizedString(singleDoor.name) })
            : t('Are you sure you want to open door', { ns: 'common' })
        }
        isLoading={isOpenLoading}
        onOk={async () => {
          if (singleDoor?.lockAddress) {
            await handleOpenDoor(singleDoor.lockAddress);
          }
        }}
        onCancel={closeConfirmModal}
      />

      <Modal isOpen={isModalOpened} onClose={handleCloseModal} closeIcon={false} paddingLess width={360} footer={false}>
        <MessagePanel
          className="w-[360px]"
          type="info"
          title={t('Choose the door you need')}
          description={t('Click the button to access')}
          actions={
            <div className="flex flex-col justify-center gap-3">
              {accessibleDoors.map((door) => (
                <Button
                  isLoading={isOpenLoading}
                  key={door.internalEquipmentId}
                  onClick={async () => {
                    if (door.lockAddress) {
                      await handleOpenDoor(door.lockAddress);
                    }
                  }}
                >
                  {getLocalizedString(door.name)}
                </Button>
              ))}

              <Button theme="outlined" onClick={handleCloseModal} isLoading={isOpenLoading} className="mt-4">
                {t('Cancel')}
              </Button>
            </div>
          }
        />
      </Modal>
    </div>
  );
};
